/**
 * purgeAuthStorage — single source of truth for clearing stored auth
 * credentials. Used by two distinct logout paths:
 *
 *   1. bootShell._forceLogoutAndReauth (pre-mount): runs BEFORE the SPA
 *      mounts, so queryClient / state aren't constructed yet.
 *   2. switchers/logout.js (post-mount): runs from a mounted shell with
 *      state + queryClient available.
 *
 * Both paths must purge the same keys to avoid one logout shape leaving
 * residue the other would pick up on the next boot.
 *
 * Keys purged:
 *   - localStorage `sb-*`           — legacy auth-js session keys (pre-retirement residue)
 *                                     (one per Supabase project ref).
 *   - localStorage `symbols_*`      — SDK access/refresh/bridge tokens.
 *   - localStorage `activeWorkspace` — the canonical chosen-workspace pointer.
 *     Cleared so a fresh sign-in (or a different user on the same browser)
 *     doesn't inherit the previous session's workspace selection (which boot
 *     prefers over the JWT claim — see _resolveAuthSnapshot in bootShell.js).
 *   - sessionStorage `__symbols_sdk_only_fed_attempted` — the per-tab
 *     flag that prevents bootShell's federation-retry loop. Cleared so
 *     a fresh sign-in isn't pre-blocked.
 *
 * Best-effort: each storage access is try/wrapped so a quota / disabled-
 * storage error in one doesn't prevent the rest from running.
 */

import { clearActiveWorkspacePointer } from './activeWorkspacePointer.js'

// FA204 / frank trap — SDK_ONLY_RETRY_KEY used to be a bare module-scope
// const, read from inside this exported function's own body: unresolvable
// once this file's exports are individually frank.toJSON'd for platform
// delivery (project_frank_drops_module_local_helpers). Inlined as a local.
export function purgeAuthStorage() {
  const SDK_ONLY_RETRY_KEY = '__symbols_sdk_only_fed_attempted'
  if (typeof window === 'undefined') return
  try {
    const purge = Object.keys(window.localStorage).filter(
      (k) => k.startsWith('sb-') || k.startsWith('symbols_')
    )
    for (const k of purge) window.localStorage.removeItem(k)
    clearActiveWorkspacePointer()
  } catch (_) {}
  try {
    window.sessionStorage.removeItem(SDK_ONLY_RETRY_KEY)
  } catch (_) {}
}
